import React, { JSX, useEffect } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FormInput } from "go-form";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { FILTER_VALUE_SEPARATOR } from "go-list/core/components/Filter/services";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	BundleApi,
	BundleItemItemApi,
	ProductApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";

interface Props {
	form: UseFormReturn<BundleApi>;
	itemIndex: number;
	drawInStockBadge: (type: "destination" | "source", product: ProductApi) => JSX.Element | undefined;
	setShouldFetchSourceProductStates: (value: boolean) => void;
}

const BundleFormItemsTableBodySubRow = ({
	form,
	itemIndex,
	drawInStockBadge,
	setShouldFetchSourceProductStates,
}: Props) => {
	const {
		watch,
		control,
		formState: { errors },
		register,
		setValue,
		getValues,
	} = form;
	const watchedItems = watch(`items.${itemIndex}.items`) || [];
	const itemsAsString = JSON.stringify(watchedItems);
	const { fields, remove, append } = useFieldArray({
		control,
		name: `items.${itemIndex}.items`,
		keyName: "key",
	});
	const organization = useSelector(selectOrganization);

	useEffect(() => {
		if (checkIfAddNewItem()) {
			append({
				product_name: null,
				product_id: null,
				quantity: null,
				unit_fifo_cost_net_money: null,
				total_fifo_cost_net_money: null,
				state: null,
			});
			setShouldFetchSourceProductStates(false);
		}
	}, [itemsAsString]);

	const checkIfAddNewItem = () => {
		const prevItem = watchedItems[watchedItems?.length - 1];

		return prevItem?.product_id;
	};

	const handleChangeProduct = (product: ProductApi, index: number) => {
		setValue(`items.${itemIndex}.items.${index}.id`, undefined);
		setValue(`items.${itemIndex}.items.${index}.product_name`, product.name);
		setValue(`items.${itemIndex}.items.${index}.product_id`, product.id, { shouldDirty: true });
		setValue(`items.${itemIndex}.items.${index}.measure_type`, product.measure_type, { shouldDirty: true });
		setValue(`items.${itemIndex}.items.${index}.state`, product.state, { shouldDirty: true });
		setValue(`items.${itemIndex}.items.${index}.unit_fifo_cost_net_money`, product.cost_price_money, {
			shouldDirty: true,
		});
		setValue(`items.${itemIndex}.items.${index}.amount`, 1, {
			shouldDirty: true,
		});

		setShouldFetchSourceProductStates(true);
	};

	const handleRemoveItem = (index: number) => {
		remove(index);
	};

	const searchProducts = async (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		const newParams = {
			...params,
			"productType|e": `COMPOSITE${FILTER_VALUE_SEPARATOR}BASIC`,
			include: "volumes",
		};
		return apiOrganization.getProductSearchSelect(search, newParams, {
			cancelToken: options?.token,
		});
	};

	const handleChangeQuantity = (index: number, item: BundleItemItemApi, value?: number) => {
		if (value === null || value === undefined) {
			return;
		}

		const itemQuantity = getValues(`items.${itemIndex}.quantity`);
		let amount = 0;

		if (value && itemQuantity) {
			amount = value / itemQuantity;
		} else if (itemQuantity === null || isNaN(Number(itemQuantity))) {
			amount = value;
		} else amount = 0;

		setValue(`items.${itemIndex}.items.${index}.amount`, amount);
	};

	return (
		<>
			{watchedItems.map((item, index) => (
				<tr key={fields[index].key} className="sub-row">
					<td className="action">
						<FormInput
							type="hidden"
							errors={errors}
							register={register}
							name={`items.${itemIndex}.items.${index}.id`}
							defaultValue={item.id}
						/>
					</td>
					<td>
						{!item?.product_id ? (
							<div className="ps-4">
								<FormSelectGroup
									name={`items.${itemIndex}.items.${index}.product_name`}
									errors={errors}
									onChange={(_, product) => handleChangeProduct(product, index)}
									defaultValue={{
										label: item?.product_name,
										id: item?.product_id,
									}}
									loadOptions={searchProducts}
									control={control}
									data-testid={`items.${itemIndex}.items.${index}.product_name`}
								/>
								<FormInput
									type="hidden"
									errors={errors}
									register={register}
									name={`items.${itemIndex}.items.${index}.product_id`}
									defaultValue={item.product_id || ""}
								/>
							</div>
						) : (
							<div className="d-flex align-items-center product-box ps-4">
								<Link
									rel="noreferrer"
									target="_blank"
									to={`/${organization.id}/products/${item.product_id}/edit`}
									className="me-2"
								>
									{item.product_name}
								</Link>
								{drawInStockBadge("source", {
									name: item.product_name,
									id: item.product_id,
									state: item.state,
									measure_type: item.measure_type,
								} as unknown as ProductApi)}
							</div>
						)}
					</td>
					<td>
						<FormNumberInput
							errors={errors}
							name={`items.${itemIndex}.items.${index}.quantity`}
							suffix=""
							defaultValue={Number(item.quantity)}
							control={control}
							allowEmpty
							onBlur={(value) => handleChangeQuantity(index, item, value)}
						/>
					</td>
					<td />
					<td className="action">
						{!((fields.length === 1 && index === 0) || index === fields.length - 1) && (
							<RemoveSVG className="icon" onClick={() => handleRemoveItem(index)} />
						)}
					</td>
				</tr>
			))}
		</>
	);
};

export default BundleFormItemsTableBodySubRow;
